import { useState } from "react";
import { useForm } from "react-hook-form";
import type { SignUpData } from "types/auth";
import "styles/cadastro.css";
import { useTranslation } from "react-i18next";

type FormData = {
  usuarioAssociado?: string;
  codigoDeAssociacao?: string;
  cnpj: string;
  cnaePrincipal: string;
  plano: string;
};


type Props = {
  data: SignUpData;
  onBack: () => void;
  onFinish: (p?: Partial<SignUpData>) => void;
  loading?: boolean;
};

export default function StepProfile({ data, onBack, onFinish }: Props) {
  const { t } = useTranslation();
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      usuarioAssociado: data.usuarioAssociado ?? "",
      codigoDeAssociacao: data.codigoDeAssociacao ?? "",
      cnpj: data.cnpj ?? "",
      cnaePrincipal: data.cnaePrincipal ?? "",
      plano: data.plano ?? "basico",
    },
  });

  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: FormData) => {
    setLoading(true);
    onFinish({
      usuarioAssociado: values.usuarioAssociado,
      codigoDeAssociacao: values.codigoDeAssociacao,
      cnpj: values.cnpj,
      cnaePrincipal: values.cnaePrincipal,
      plano: values.plano,
    });
    setLoading(false);
  };

  const tipoDocumento = data.tipoDocumento ?? "cnpj";

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="cadastro-grid-campos">
        {tipoDocumento === "estrangeiro" && (
          <div className="cadastro-campo col-span-2">
            <label htmlFor="cnpj" className="cadastro-rotulo">{t("fields.documento_estrangeiro")}</label>
            <input
              id="cnpj"
              className="cadastro-entrada"
              placeholder={t("placeholders.documento_estrangeiro")}
              autoComplete="off"
              type="text"
              {...register("cnpj", {
                required: true,
              })}
            />
            {errors.cnpj && <span className="cadastro-erro">{t("validations.required")}</span>}
          </div>
        )}

        {tipoDocumento === "cpf" && (
          <div className="cadastro-campo col-span-2">
            <label htmlFor="cnpj" className="cadastro-rotulo">{t("fields.cpf")}</label>
            <input
              id="cnpj"
              className="cadastro-entrada"
              placeholder={t("placeholders.cpf")}
              autoComplete="off"
              inputMode="numeric"
              maxLength={11}
              type="text"
              {...register("cnpj", {
                onChange: (e) => {
                  e.target.value = String(e.target.value ?? "").replace(/\D/g, "").slice(0, 11);
                },
                validate: (value) => {
                  const v = String(value ?? "");
                  if (v === "") return true;
                  return /^\d{11}$/.test(v) || t("validations.cpf_11");
                },
              })}
            />
            {errors.cnpj && <span className="cadastro-erro">{errors.cnpj.message}</span>}
          </div>
        )}

        {tipoDocumento === "cnpj" && (
          <>
            <div className="cadastro-campo">
              <label htmlFor="cnpj" className="cadastro-rotulo">{t("fields.cnpj")}</label>
              <input
                id="cnpj"
                className="cadastro-entrada"
                placeholder={t("placeholders.cnpj")}
                autoComplete="off"
                inputMode="numeric"
                maxLength={14}
                type="text"
                {...register("cnpj", {
                  onChange: (e) => {
                    e.target.value = String(e.target.value ?? "").replace(/\D/g, "").slice(0, 14);
                  },
                  validate: (value) => {
                    const v = String(value ?? "");
                    if (v === "") return true;
                    return /^\d{14}$/.test(v) || t("validations.cnpj_14");
                  },
                })}
              />
              {errors.cnpj && <span className="cadastro-erro">{errors.cnpj.message}</span>}
            </div>

            <div className="cadastro-campo">
              <label htmlFor="cnaePrincipal" className="cadastro-rotulo">{t("fields.cnae_code_optional")}</label>
              <input
                id="cnaePrincipal"
                className="cadastro-entrada"
                placeholder={t("placeholders.cnae")}
                autoComplete="off"
                inputMode="numeric"
                maxLength={7}
                type="text"
                {...register("cnaePrincipal", {
                  onChange: (e) => {
                    e.target.value = String(e.target.value ?? "").replace(/\D/g, "").slice(0, 7);
                  },
                  validate: (value) => {
                    const v = String(value ?? "");
                    if (v === "") return true;
                    return /^\d{5}$|^\d{7}$/.test(v) || t("validations.cnae_5_or_7");
                  },
                })}
              />
            </div>
          </>
        )}

        <div className="cadastro-campo col-span-2">
          <label htmlFor="plano" className="cadastro-rotulo">{t("fields.account_type")}</label>
          <select id="plano" className="cadastro-selecao" {...register("plano", { required: true })}>
            <option value="basico">{t("basico")}</option>
            <option value="gold">{t("gold")}</option>
            <option value="premium">{t("premium")}</option>
          </select>
        </div>

        <div className="cadastro-campo">
          <label htmlFor="usuarioAssociado" className="cadastro-rotulo">
            {t("fields.user_associated_optional")}
          </label>
          <input
            id="usuarioAssociado"
            className="cadastro-entrada"
            placeholder={t("placeholders.email_or_id")}
            autoComplete="email"
            {...register("usuarioAssociado")}
          />
        </div>

        <div className="cadastro-campo">
          <label htmlFor="codigoDeAssociacao" className="cadastro-rotulo">
            {t("fields.association_code_optional")}
          </label>
          <input
            id="codigoDeAssociacao"
            className="cadastro-entrada"
            placeholder={t("placeholders.code_example")}
            autoComplete="one-time-code"
            {...register("codigoDeAssociacao")}
          />
        </div>
      </div>

      <div className="cadastro-acoes">
        <button type="button" className="cadastro-btn-ghost" onClick={onBack} disabled={loading}>
          {t("buttons.back")}
        </button>
        <button type="submit" className="cadastro-btn-primario" disabled={loading}>
          {t("buttons.continue")}
        </button>
      </div>
    </form>
  );
}
